import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import {
  Box,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Map as GoogleMap, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";
import { DEFAULT_CENTER, DEFAULT_ZOOM } from "../../mission-map/constants";
import type {
  StatisticsGeographicInsightsResult,
  StatisticsGeographicPoint,
} from "../types";

const MAP_ID = "aeb6dd73292717b9d8b403f4";

const ISRAEL_BOUNDS = {
  latLngBounds: { south: 29.45, west: 34.22, north: 33.42, east: 35.92 },
  strictBounds: false,
} as const;

type LayerMode = "both" | "volunteers" | "missions";

type Rgb = { r: number; g: number; b: number };

const VOLUNTEER_RGB: Rgb = { r: 6, g: 91, b: 249 };
const MISSION_RGB: Rgb = { r: 230, g: 81, b: 0 };

/** Soft blue density — volunteer supply. */
const VOLUNTEER_GRADIENT = [
  "rgba(0, 0, 0, 0)",
  "rgba(144, 202, 249, 0.35)",
  "rgba(6, 91, 249, 0.55)",
  "rgba(5, 32, 76, 0.82)",
];

/** Warm coral density — open mission demand. */
const MISSION_GRADIENT = [
  "rgba(0, 0, 0, 0)",
  "rgba(255, 224, 178, 0.32)",
  "rgba(255, 138, 101, 0.52)",
  "rgba(198, 40, 40, 0.78)",
];

type DensityHeatmapOverlayOptions = {
  maxIntensity: number;
  rgb: Rgb;
  opacity: number;
  radiusPx: number;
};

/**
 * Canvas heatmap overlay (replaces deprecated google.maps.visualization.HeatmapLayer).
 * Defined lazily so `google` exists only after APIProvider loads the Maps script.
 * @see https://developers.google.com/maps/documentation/javascript/heatmaplayer
 */
function createDensityCanvasHeatmap(
  points: StatisticsGeographicPoint[],
  options: DensityHeatmapOverlayOptions,
): google.maps.OverlayView {
  class DensityCanvasHeatmap extends google.maps.OverlayView {
    private canvas: HTMLCanvasElement | null = null;

    constructor(
      private readonly heatPoints: StatisticsGeographicPoint[],
      private readonly heatOptions: DensityHeatmapOverlayOptions,
    ) {
      super();
    }

    onAdd() {
      this.canvas = document.createElement("canvas");
      this.canvas.style.position = "absolute";
      this.canvas.style.pointerEvents = "none";
      this.getPanes()?.overlayLayer?.appendChild(this.canvas);
    }

    onRemove() {
      this.canvas?.remove();
      this.canvas = null;
    }

    draw() {
      const projection = this.getProjection();
      const map = this.getMap();
      if (!projection || !map || !this.canvas) return;
      if (!(map instanceof google.maps.Map)) return;

      const bounds = map.getBounds();
      if (!bounds) return;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const topLeft = projection.fromLatLngToDivPixel(
        new google.maps.LatLng(ne.lat(), sw.lng()),
      );
      const bottomRight = projection.fromLatLngToDivPixel(
        new google.maps.LatLng(sw.lat(), ne.lng()),
      );
      if (!topLeft || !bottomRight) return;

      const width = Math.max(1, Math.ceil(Math.abs(bottomRight.x - topLeft.x)));
      const height = Math.max(1, Math.ceil(Math.abs(bottomRight.y - topLeft.y)));

      this.canvas.width = width;
      this.canvas.height = height;
      this.canvas.style.left = `${topLeft.x}px`;
      this.canvas.style.top = `${topLeft.y}px`;
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;

      const ctx = this.canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, width, height);

      const { radiusPx, maxIntensity, rgb, opacity } = this.heatOptions;
      const maxW = Math.max(maxIntensity, 1);

      for (const p of this.heatPoints) {
        const pixel = projection.fromLatLngToDivPixel(
          new google.maps.LatLng(p.lat, p.lng),
        );
        if (!pixel) continue;

        const x = pixel.x - topLeft.x;
        const y = pixel.y - topLeft.y;
        if (x < -radiusPx || y < -radiusPx || x > width + radiusPx || y > height + radiusPx) {
          continue;
        }

        const t = Math.min(1, p.weight / maxW);
        const r = radiusPx * (0.55 + 0.85 * t);
        const a = opacity * (0.2 + 0.8 * t);

        const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},${a})`);
        grad.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  return new DensityCanvasHeatmap(points, options);
}

function DensityHeatmapOverlay({
  points,
  rgb,
  visible,
  radiusPx = 42,
  opacity = 0.55,
}: {
  points: StatisticsGeographicPoint[];
  rgb: Rgb;
  visible: boolean;
  radiusPx?: number;
  opacity?: number;
}) {
  const map = useMap();
  const mapsLibrary = useMapsLibrary("maps");
  const overlayRef = useRef<google.maps.OverlayView | null>(null);

  const maxIntensity = useMemo(
    () => (points.length ? Math.max(...points.map((p) => p.weight)) : 1),
    [points],
  );

  useEffect(() => {
    if (!map || !mapsLibrary) return;

    overlayRef.current?.setMap(null);
    overlayRef.current = null;

    if (!visible || !points.length) return;

    const overlay = createDensityCanvasHeatmap(points, {
      maxIntensity,
      rgb,
      opacity,
      radiusPx,
    });
    overlay.setMap(map);
    overlayRef.current = overlay;

    const redraw = () => overlay.draw();
    const idleListener = map.addListener("idle", redraw);
    const zoomListener = map.addListener("zoom_changed", redraw);

    return () => {
      google.maps.event.removeListener(idleListener);
      google.maps.event.removeListener(zoomListener);
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, mapsLibrary, maxIntensity, opacity, points, radiusPx, rgb, visible]);

  return null;
}

function SummaryStat({
  dotColor,
  icon,
  label,
  value,
  sub,
}: {
  dotColor: string;
  icon: ReactNode;
  label: string;
  value: number;
  sub?: string;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 140,
        px: 2,
        py: 1.25,
        borderRadius: 2,
        bgcolor: "rgba(255,255,255,0.92)",
        border: "1px solid",
        borderColor: "divider",
        backdropFilter: "blur(6px)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: dotColor,
            flexShrink: 0,
          }}
        />
        {icon}
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="h6" fontWeight={700} color="#05204c" lineHeight={1.2}>
        {value.toLocaleString("he-IL")}
      </Typography>
      {sub ? (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      ) : null}
    </Box>
  );
}

interface StatisticsGeographicHeatMapProps {
  data?: StatisticsGeographicInsightsResult;
  isLoading?: boolean;
}

export const StatisticsGeographicHeatMap: FC<StatisticsGeographicHeatMapProps> = ({
  data,
  isLoading,
}) => {
  const [layer, setLayer] = useState<LayerMode>("both");

  const hasVolunteers = (data?.volunteerCount ?? 0) > 0;
  const hasMissions = (data?.openOpportunityCount ?? 0) > 0;
  const isEmpty = !hasVolunteers && !hasMissions;

  const showVolunteers = layer !== "missions" && hasVolunteers;
  const showMissions = layer !== "volunteers" && hasMissions;

  const aggregationNote = data?.aggregationCellKm
    ? `הצפיפות מחושבת מכל הנתונים — מקובצים לתאים של ~${data.aggregationCellKm} ק״מ (לא דגימה).`
    : null;

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 440,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} sx={{ color: "#05204c" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "flex-start" }}
        sx={{ mb: 2 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="#05204c"
            sx={{ mb: 0.5 }}
          >
            מפת חום: מתנדבים מול משימות פתוחות
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
            צפיפות לפי מיקום אמיתי — כל המתנדבים והמשימות נכללים, מקובצים לתאי
            רשת קטנים. משימות: רק פתוחות (כמו לשונית &quot;פתוחות&quot;).
          </Typography>
        </Box>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={layer}
          onChange={(_, v: LayerMode | null) => v && setLayer(v)}
          sx={{
            alignSelf: { md: "center" },
            bgcolor: "background.paper",
            borderRadius: 999,
            p: 0.35,
            border: "1px solid",
            borderColor: "divider",
            "& .MuiToggleButton-root": {
              border: 0,
              borderRadius: 999,
              px: 1.75,
              py: 0.65,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8125rem",
              "&.Mui-selected": {
                bgcolor: "#05204c",
                color: "#fff",
                "&:hover": { bgcolor: "#0a3a8a" },
              },
            },
          }}
        >
          <ToggleButton value="both">שניהם</ToggleButton>
          <ToggleButton value="volunteers">מתנדבים</ToggleButton>
          <ToggleButton value="missions">משימות פתוחות</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <SummaryStat
          dotColor="#065bf9"
          icon={<GroupsOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
          label="מתנדבים עם מיקום"
          value={data?.volunteerCount ?? 0}
          sub={
            data?.volunteersWithoutLocation
              ? `${data.volunteersWithoutLocation.toLocaleString("he-IL")} ללא מיקום`
              : undefined
          }
        />
        <SummaryStat
          dotColor="#e65100"
          icon={<PlaceOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
          label="משימות פתוחות עם מיקום"
          value={data?.openOpportunityCount ?? 0}
          sub={
            data?.openOpportunitiesWithoutLocation
              ? `${data.openOpportunitiesWithoutLocation.toLocaleString("he-IL")} ללא מיקום`
              : undefined
          }
        />
      </Stack>

      <Box
        sx={{
          position: "relative",
          height: { xs: 380, md: 480 },
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 8px 32px rgba(5, 32, 76, 0.08)",
        }}
      >
        {isEmpty ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%", bgcolor: "action.hover", px: 2 }}
          >
            <Typography color="text.secondary" textAlign="center">
              אין מספיק נתונים עם מיקום להצגת מפת חום
            </Typography>
          </Stack>
        ) : (
          <GoogleMap
            defaultCenter={DEFAULT_CENTER}
            defaultZoom={DEFAULT_ZOOM}
            minZoom={6.8}
            maxZoom={12}
            gestureHandling="cooperative"
            disableDefaultUI
            zoomControl
            restriction={ISRAEL_BOUNDS}
            mapId={MAP_ID}
            style={{ width: "100%", height: "100%" }}
          >
            <DensityHeatmapOverlay
              points={data?.volunteers ?? []}
              rgb={VOLUNTEER_RGB}
              visible={showVolunteers}
            />
            <DensityHeatmapOverlay
              points={data?.openOpportunities ?? []}
              rgb={MISSION_RGB}
              visible={showMissions}
              radiusPx={38}
              opacity={0.5}
            />
          </GoogleMap>
        )}

        {!isEmpty ? (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              position: "absolute",
              bottom: 12,
              left: 12,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.94)",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            }}
          >
            {showVolunteers ? (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: 56,
                    height: 8,
                    borderRadius: 1,
                    background: `linear-gradient(to right, ${VOLUNTEER_GRADIENT.slice(1).join(", ")})`,
                  }}
                />
                <Typography variant="caption" fontWeight={600}>
                  מתנדבים
                </Typography>
              </Stack>
            ) : null}
            {showMissions ? (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: 56,
                    height: 8,
                    borderRadius: 1,
                    background: `linear-gradient(to right, ${MISSION_GRADIENT.slice(1).join(", ")})`,
                  }}
                />
                <Typography variant="caption" fontWeight={600}>
                  משימות פתוחות
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        ) : null}
      </Box>

      {aggregationNote ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {aggregationNote}
        </Typography>
      ) : null}
    </Box>
  );
};
